import React from 'react';
import { Clock, User, Loader2, Plus, CalendarOff, Palmtree, XCircle, RefreshCcw, Moon, DoorOpen, ClipboardList, Users } from 'lucide-react';
import { ScheduleEntry, DAY_OF_WEEK_LABELS, BlockedDate, RamadanPeriod, scheduleService } from '../../services/scheduleService';
import type { ModuleElement } from '@/types/filiere.types';

export interface CanceledSession {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  cancellationReason?: string;
  moduleElementName?: string;
  sessionTypeName?: string;
  sessionTypeColor?: string;
}

// Helper to check if a session was rescheduled (vs simply canceled)
const isRescheduledSession = (session: CanceledSession): boolean => {
  return session.cancellationReason?.toLowerCase().startsWith('report') || false;
};

interface TimetableGridProps {
  entries: ScheduleEntry[];
  canceledSessions?: CanceledSession[];
  weekStart: string;
  onEntryClick: (entry: ScheduleEntry, sessionDate?: string) => void;
  onCellClick?: (dayOfWeek: number, startTime: string) => void;
  onDrop?: (dayOfWeek: number, startTime: string) => void;
  draggedMatiere: ModuleElement | null;
  dropTarget: { dayOfWeek: number; startTime: string } | null;
  setDropTarget: (target: { dayOfWeek: number; startTime: string } | null) => void;
  loading?: boolean;
  blockedDates?: BlockedDate[];
  ramadanPeriod?: RamadanPeriod | null;
}

// Time slots from 8:00 to 21:00 (9pm)
const TIME_SLOTS = Array.from({ length: 14 }, (_, i) => {
  const hour = 8 + i;
  return `${hour.toString().padStart(2, '0')}:00`;
});

// Days Monday (1) to Sunday (0) - reordered for display
const DAYS = [1, 2, 3, 4, 5, 6, 0]; // Mon, Tue, Wed, Thu, Fri, Sat, Sun

// Row height in pixels (reduced for compact view)
const ROW_HEIGHT = 44; // h-11 = 2.75rem = 44px

export const TimetableGrid: React.FC<TimetableGridProps> = ({
  entries,
  canceledSessions = [],
  weekStart,
  onEntryClick,
  onCellClick,
  onDrop,
  draggedMatiere,
  dropTarget,
  setDropTarget,
  loading = false,
  blockedDates = [],
  ramadanPeriod,
}) => {
  const getDateForDay = (dayOfWeek: number) => {
    const date = new Date(weekStart);
    const currentDayOfWeek = date.getDay();
    // Handle Sunday (0) specially - it's at the end of the week
    const targetDay = dayOfWeek === 0 ? 7 : dayOfWeek;
    const currentDay = currentDayOfWeek === 0 ? 7 : currentDayOfWeek;
    const diff = targetDay - currentDay;
    date.setDate(date.getDate() + diff);
    return date;
  };

  const isToday = (dayOfWeek: number) => {
    const today = new Date();
    const date = getDateForDay(dayOfWeek);
    return (
      today.getDate() === date.getDate() &&
      today.getMonth() === date.getMonth() &&
      today.getFullYear() === date.getFullYear()
    );
  };

  // Check if a specific day is blocked (holiday or vacation)
  const getBlockedInfo = (dayOfWeek: number): BlockedDate | undefined => {
    const date = getDateForDay(dayOfWeek);
    const dateStr = date.toISOString().split('T')[0];
    return blockedDates.find(b => b.date === dateStr);
  };

  // Check if a specific day is during Ramadan
  const isRamadanDay = (dayOfWeek: number): boolean => {
    if (!ramadanPeriod) return false;
    const date = getDateForDay(dayOfWeek);
    const dateStr = date.toISOString().split('T')[0];
    return dateStr >= ramadanPeriod.startDate && dateStr <= ramadanPeriod.endDate;
  };

  // Get the effective session times for an entry on a specific date (accounts for Ramadan adjustments)
  const getSessionTimesForDate = (entry: ScheduleEntry, date: Date): { startTime: string; endTime: string } | null => {
    const session = scheduleService.getSessionForDate(entry, date);
    if (session && !session.isCancelled) {
      // Use the session's actual times (which may differ from entry times during Ramadan)
      return { startTime: session.startTime, endTime: session.endTime };
    }
    return null;
  };

  // Get the professor for a specific session date (accounts for professor overrides)
  const getSessionProfessorForDate = (entry: ScheduleEntry, date: Date): { firstName?: string; lastName?: string } | null => {
    const session = scheduleService.getSessionForDate(entry, date);
    if (session && !session.isCancelled) {
      // If session has an override professor, use that
      if (session.overrideProfessor) {
        return session.overrideProfessor;
      }
      // If session has a professorIdOverride but no overrideProfessor data, the entry professor is the fallback
      // (overrideProfessor should be populated by the backend if professorIdOverride exists)
    }
    // Default to entry's professor
    return entry.professor || null;
  };

  // Get the room for a specific session date (accounts for room overrides)
  const getSessionRoomForDate = (entry: ScheduleEntry, date: Date): { name?: string } | null => {
    const session = scheduleService.getSessionForDate(entry, date);
    if (session && !session.isCancelled) {
      // If session has an override room, use that
      if (session.overrideRoom) {
        return session.overrideRoom;
      }
    }
    // Default to entry's room
    return entry.room || null;
  };

  const getEntriesForDayAndTime = (dayOfWeek: number, timeSlot: string) => {
    const slotHour = parseInt(timeSlot.split(':')[0]);
    const dateForDay = getDateForDay(dayOfWeek);

    return entries.filter((entry) => {
      // Match on the cell's concrete DATE, not entry.dayOfWeek — rescheduled
      // (exception) sessions can land on a different weekday than their entry

      // Check if entry has sessions - if so, only show if there's a valid session on this date
      if (entry.sessions && entry.sessions.length > 0) {
        const sessionTimes = getSessionTimesForDate(entry, dateForDay);
        if (!sessionTimes) return false;

        // Use the session's actual start time (which may differ from entry during Ramadan)
        const sessionStartHour = parseInt(sessionTimes.startTime.split(':')[0]);
        return sessionStartHour === slotHour;
      }

      // Entries without sessions should not be displayed
      return false;
    });
  };

  // Calculate height based on session times for the specific date
  const getEntryHeightForDate = (entry: ScheduleEntry, date: Date) => {
    const sessionTimes = getSessionTimesForDate(entry, date);
    const startTime = sessionTimes?.startTime || entry.startTime;
    const endTime = sessionTimes?.endTime || entry.endTime;

    const startParts = startTime.split(':');
    const endParts = endTime.split(':');
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    const durationHours = (endMinutes - startMinutes) / 60;
    return durationHours * ROW_HEIGHT;
  };

  // Calculate top offset based on session start time for the specific date
  const getEntryTopOffsetForDate = (entry: ScheduleEntry, date: Date) => {
    const sessionTimes = getSessionTimesForDate(entry, date);
    const startTime = sessionTimes?.startTime || entry.startTime;

    const startParts = startTime.split(':');
    const minutes = parseInt(startParts[1]);
    return (minutes / 60) * ROW_HEIGHT;
  };

  // Check if a cell has an entry that spans over it (uses session times for accuracy)
  const hasEntryAt = (dayOfWeek: number, timeSlot: string) => {
    const slotHour = parseInt(timeSlot.split(':')[0]);
    const dateForDay = getDateForDay(dayOfWeek);

    return entries.some((entry) => {
      // Match on the cell's date, not entry.dayOfWeek (rescheduled sessions)
      // Check if entry has sessions - if so, only count if there's a valid session on this date
      if (entry.sessions && entry.sessions.length > 0) {
        const sessionTimes = getSessionTimesForDate(entry, dateForDay);
        if (!sessionTimes) return false;

        // Use actual session times (which may differ during Ramadan)
        const sessionStartHour = parseInt(sessionTimes.startTime.split(':')[0]);
        const sessionEndHour = parseInt(sessionTimes.endTime.split(':')[0]);
        const sessionEndMinutes = parseInt(sessionTimes.endTime.split(':')[1]);

        // Adjust end hour if there are minutes (e.g., 10:30 should cover hour 10)
        const effectiveEndHour = sessionEndMinutes > 0 ? sessionEndHour + 1 : sessionEndHour;

        return slotHour >= sessionStartHour && slotHour < effectiveEndHour;
      }

      return false;
    });
  };

  // Get canceled sessions for a specific day and time slot
  const getCanceledSessionsForDayAndTime = (dayOfWeek: number, timeSlot: string) => {
    const date = getDateForDay(dayOfWeek);
    const dateStr = date.toISOString().split('T')[0];
    const slotHour = parseInt(timeSlot.split(':')[0]);

    return canceledSessions.filter((session) => {
      if (session.date !== dateStr) return false;
      const sessionStartHour = parseInt(session.startTime.split(':')[0]);
      return sessionStartHour === slotHour;
    });
  };

  // Calculate height for canceled session (similar to entry)
  const getCanceledSessionHeight = (session: CanceledSession) => {
    const startParts = session.startTime.split(':');
    const endParts = session.endTime.split(':');
    const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
    const endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
    const durationHours = (endMinutes - startMinutes) / 60;
    return durationHours * ROW_HEIGHT;
  };

  // Calculate top offset for canceled session
  const getCanceledSessionTopOffset = (session: CanceledSession) => {
    const startParts = session.startTime.split(':');
    const minutes = parseInt(startParts[1]);
    return (minutes / 60) * ROW_HEIGHT;
  };

  const handleDragOver = (e: React.DragEvent, dayOfWeek: number, startTime: string) => {
    e.preventDefault();
    if (draggedMatiere && !hasEntryAt(dayOfWeek, startTime)) {
      setDropTarget({ dayOfWeek, startTime });
    }
  };

  const handleDragLeave = () => {
    setDropTarget(null);
  };

  const handleDropOnCell = (e: React.DragEvent, dayOfWeek: number, startTime: string) => {
    e.preventDefault();
    if (onDrop && draggedMatiere && !hasEntryAt(dayOfWeek, startTime)) {
      onDrop(dayOfWeek, startTime);
    }
    setDropTarget(null);
  };

  const handleCellClick = (dayOfWeek: number, startTime: string) => {
    if (onCellClick && !hasEntryAt(dayOfWeek, startTime)) {
      onCellClick(dayOfWeek, startTime);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="overflow-auto h-full">
      <div className="min-w-[850px]">
        {/* Header */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)] border-b border-gray-200 bg-gray-50 sticky top-0 z-10">
          <div className="p-1.5 text-center text-[10px] font-medium text-gray-500">
            Heure
          </div>
          {DAYS.map((dayOfWeek) => {
            const date = getDateForDay(dayOfWeek);
            const today = isToday(dayOfWeek);
            const blockedInfo = getBlockedInfo(dayOfWeek);
            const isBlocked = !!blockedInfo;
            const isRamadan = isRamadanDay(dayOfWeek);
            return (
              <div
                key={dayOfWeek}
                className={`p-1.5 text-center border-l border-gray-200 ${
                  isBlocked
                    ? blockedInfo.type === 'HOLIDAY'
                      ? 'bg-amber-50'
                      : blockedInfo.type === 'EXAM_PERIOD'
                      ? 'bg-purple-50'
                      : 'bg-green-50'
                    : isRamadan
                    ? 'bg-emerald-50'
                    : today
                    ? 'bg-primary-50'
                    : ''
                }`}
              >
                <p
                  className={`text-[10px] font-medium ${
                    isBlocked
                      ? blockedInfo.type === 'HOLIDAY'
                        ? 'text-amber-700'
                        : blockedInfo.type === 'EXAM_PERIOD'
                        ? 'text-purple-700'
                        : 'text-green-700'
                      : isRamadan
                      ? 'text-emerald-700'
                      : today
                      ? 'text-primary-700'
                      : 'text-gray-500'
                  }`}
                >
                  {DAY_OF_WEEK_LABELS[dayOfWeek]}
                  {isRamadan && !isBlocked && (
                    <Moon size={10} className="inline-block ml-1 text-emerald-600" />
                  )}
                </p>
                <p
                  className={`text-sm font-bold ${
                    isBlocked
                      ? blockedInfo.type === 'HOLIDAY'
                        ? 'text-amber-900'
                        : blockedInfo.type === 'EXAM_PERIOD'
                        ? 'text-purple-900'
                        : 'text-green-900'
                      : isRamadan
                      ? 'text-emerald-900'
                      : today
                      ? 'text-primary-900'
                      : 'text-gray-900'
                  }`}
                >
                  {date.getDate()}
                </p>
                {isBlocked && (
                  <div className="flex items-center justify-center gap-0.5 mt-0.5">
                    {blockedInfo.type === 'HOLIDAY' ? (
                      <CalendarOff size={10} className="text-amber-600" />
                    ) : blockedInfo.type === 'EXAM_PERIOD' ? (
                      <ClipboardList size={10} className="text-purple-700" />
                    ) : (
                      <Palmtree size={10} className="text-green-600" />
                    )}
                    <span
                      className={`text-[9px] font-medium truncate max-w-[60px] ${
                        blockedInfo.type === 'HOLIDAY' ? 'text-amber-600' : blockedInfo.type === 'EXAM_PERIOD' ? 'text-purple-700' : 'text-green-600'
                      }`}
                      title={blockedInfo.name}
                    >
                      {blockedInfo.name}
                    </span>
                  </div>
                )}
                {isRamadan && !isBlocked && (
                  <div className="flex items-center justify-center gap-0.5 mt-0.5">
                    <span className="text-[9px] font-medium text-emerald-600" title="Période Ramadan">
                      Ramadan
                    </span>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Time Grid */}
        <div className="grid grid-cols-[50px_repeat(7,1fr)]">
          {TIME_SLOTS.map((timeSlot) => (
            <React.Fragment key={timeSlot}>
              {/* Time Label */}
              <div className="h-11 border-b border-gray-100 px-1 py-0.5 flex items-start">
                <span className="text-[10px] text-gray-500">{timeSlot}</span>
              </div>

              {/* Day Cells */}
              {DAYS.map((dayOfWeek) => {
                const cellEntries = getEntriesForDayAndTime(dayOfWeek, timeSlot);
                const cellCanceledSessions = getCanceledSessionsForDayAndTime(dayOfWeek, timeSlot);
                const today = isToday(dayOfWeek);
                const blockedInfo = getBlockedInfo(dayOfWeek);
                const isBlocked = !!blockedInfo;
                const isRamadan = isRamadanDay(dayOfWeek);
                const isDropTarget =
                  dropTarget?.dayOfWeek === dayOfWeek &&
                  dropTarget?.startTime === timeSlot &&
                  !isBlocked;
                const hasEntry = hasEntryAt(dayOfWeek, timeSlot);
                const canInteract = onCellClick && !hasEntry && !isBlocked;

                return (
                  <div
                    key={`${dayOfWeek}-${timeSlot}`}
                    className={`h-11 border-b border-l border-gray-100 relative group transition-colors ${
                      isBlocked
                        ? blockedInfo.type === 'HOLIDAY'
                          ? 'bg-amber-50/50 cursor-not-allowed'
                          : blockedInfo.type === 'EXAM_PERIOD'
                          ? 'bg-purple-50/60 cursor-not-allowed'
                          : 'bg-green-50/50 cursor-not-allowed'
                        : isRamadan
                        ? 'bg-emerald-50/30'
                        : today
                        ? 'bg-primary-50/30'
                        : ''
                    } ${isDropTarget ? 'bg-primary-100 ring-2 ring-primary-400 ring-inset' : ''} ${
                      canInteract ? 'cursor-pointer hover:bg-gray-50' : ''
                    }`}
                    style={isBlocked ? {
                      backgroundImage: blockedInfo.type === 'HOLIDAY'
                        ? 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(245, 158, 11, 0.1) 5px, rgba(245, 158, 11, 0.1) 10px)'
                        : blockedInfo.type === 'EXAM_PERIOD'
                        ? 'repeating-linear-gradient(-45deg, transparent, transparent 6px, rgba(147, 51, 234, 0.14) 6px, rgba(147, 51, 234, 0.14) 12px)'
                        : 'repeating-linear-gradient(45deg, transparent, transparent 5px, rgba(16, 185, 129, 0.1) 5px, rgba(16, 185, 129, 0.1) 10px)',
                    } : undefined}
                    onDragOver={(e) => !isBlocked && handleDragOver(e, dayOfWeek, timeSlot)}
                    onDragLeave={handleDragLeave}
                    onDrop={(e) => !isBlocked && handleDropOnCell(e, dayOfWeek, timeSlot)}
                    onClick={() => !isBlocked && handleCellClick(dayOfWeek, timeSlot)}
                  >
                    {/* Blocked indicator for first time slot only */}
                    {isBlocked && timeSlot === '08:00' && (
                      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <div className={`flex flex-col items-center ${
                          blockedInfo.type === 'HOLIDAY' ? 'text-amber-400' : blockedInfo.type === 'EXAM_PERIOD' ? 'text-purple-400' : 'text-green-400'
                        }`}>
                          {blockedInfo.type === 'HOLIDAY' ? (
                            <CalendarOff size={16} />
                          ) : blockedInfo.type === 'EXAM_PERIOD' ? (
                            <>
                              <ClipboardList size={18} className="text-purple-500" />
                              <span className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-purple-600">Examens</span>
                            </>
                          ) : (
                            <Palmtree size={16} />
                          )}
                        </div>
                      </div>
                    )}

                    {/* Add button on hover */}
                    {canInteract && !draggedMatiere && (
                      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="p-1 bg-primary-100 rounded-full text-primary-600">
                          <Plus size={12} />
                        </div>
                      </div>
                    )}

                    {/* Drop indicator */}
                    {isDropTarget && (
                      <div className="absolute inset-0.5 flex items-center justify-center border-2 border-dashed border-primary-400 rounded bg-primary-50/50">
                        <span className="text-[10px] font-medium text-primary-600">
                          Déposer
                        </span>
                      </div>
                    )}

                    {/* Canceled/Rescheduled Sessions (faded background) */}
                    {cellCanceledSessions.map((session) => {
                      const isRescheduled = isRescheduledSession(session);

                      return (
                        <div
                          key={`canceled-${session.id}`}
                          className="absolute left-0.5 right-0.5 overflow-hidden rounded border-l-2 opacity-50 pointer-events-none"
                          style={{
                            top: `${getCanceledSessionTopOffset(session)}px`,
                            height: `${getCanceledSessionHeight(session) - 2}px`,
                            borderLeftColor: isRescheduled ? '#F59E0B' : (session.sessionTypeColor || '#9CA3AF'),
                            backgroundColor: isRescheduled
                              ? 'rgba(245, 158, 11, 0.15)'
                              : `${session.sessionTypeColor || '#9CA3AF'}15`,
                            backgroundImage: isRescheduled
                              ? 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(245, 158, 11, 0.1) 3px, rgba(245, 158, 11, 0.1) 6px)'
                              : 'repeating-linear-gradient(45deg, transparent, transparent 3px, rgba(156, 163, 175, 0.15) 3px, rgba(156, 163, 175, 0.15) 6px)',
                            zIndex: 1,
                          }}
                          title={isRescheduled
                            ? `Reporté: ${session.cancellationReason?.replace(/^report:?\s*/i, '') || 'Séance reportée'}`
                            : (session.cancellationReason ? `Annulé: ${session.cancellationReason}` : 'Séance annulée')
                          }
                        >
                          <div className="flex flex-col h-full overflow-hidden p-1">
                            {/* Status indicator */}
                            <div className={`flex items-center gap-0.5 text-[9px] flex-shrink-0 ${
                              isRescheduled ? 'text-amber-500' : 'text-gray-400'
                            }`}>
                              {isRescheduled ? (
                                <>
                                  <RefreshCcw size={8} />
                                  <span className="font-medium">Reporté</span>
                                </>
                              ) : (
                                <>
                                  <XCircle size={8} />
                                  <span>Annulé</span>
                                </>
                              )}
                            </div>

                            {/* Module Name */}
                            <p className={`font-medium text-[10px] truncate leading-tight line-through ${
                              isRescheduled ? 'text-amber-500/70' : 'text-gray-400'
                            }`}>
                              {session.moduleElementName || 'Matière'}
                            </p>

                            {/* Session Type Badge */}
                            {session.sessionTypeName && getCanceledSessionHeight(session) > 60 && (
                              <span
                                className="inline-flex items-center px-0.5 py-0 rounded text-[8px] font-medium mt-auto w-fit opacity-60"
                                style={{
                                  backgroundColor: isRescheduled
                                    ? 'rgba(245, 158, 11, 0.25)'
                                    : `${session.sessionTypeColor || '#9CA3AF'}25`,
                                  color: isRescheduled ? '#F59E0B' : (session.sessionTypeColor || '#9CA3AF'),
                                }}
                              >
                                {session.sessionTypeName}
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}

                    {/* Entries */}
                    {cellEntries.map((entry, entryIndex) => {
                      // Get the session date for this specific cell
                      const cellDate = getDateForDay(dayOfWeek);
                      const cellDateStr = cellDate.toISOString().split('T')[0];

                      // Get the actual session times for this date (may differ during Ramadan)
                      const sessionTimes = getSessionTimesForDate(entry, cellDate);
                      const displayStartTime = sessionTimes?.startTime || entry.startTime;
                      const displayEndTime = sessionTimes?.endTime || entry.endTime;

                      // Calculate height and offset based on actual session times
                      const entryHeight = getEntryHeightForDate(entry, cellDate);
                      const entryTopOffset = getEntryTopOffsetForDate(entry, cellDate);

                      return (
                      <button
                        key={entry.id}
                        onClick={(e) => {
                          e.stopPropagation();
                          onEntryClick(entry, cellDateStr);
                        }}
                        className="absolute left-0.5 right-0.5 overflow-hidden rounded border-l-2 shadow-sm hover:shadow-md transition-all text-left p-1 hover:z-20"
                        style={{
                          top: `${entryTopOffset}px`,
                          height: `${entryHeight - 2}px`,
                          borderLeftColor: entry.sessionType?.color || '#6366F1',
                          backgroundColor: `${entry.sessionType?.color || '#6366F1'}15`,
                          zIndex: 10 + entryIndex,
                        }}
                      >
                        <div className="flex flex-col h-full overflow-hidden">
                          {/* Time - show actual session times */}
                          <div className="flex items-center gap-0.5 text-[9px] text-gray-500 flex-shrink-0">
                            <Clock size={8} />
                            <span>
                              {displayStartTime} - {displayEndTime}
                            </span>
                          </div>

                          {/* Module Name */}
                          <p className="font-medium text-gray-900 text-[10px] truncate leading-tight">
                            {entry.moduleElement?.name || 'Matière'}
                          </p>

                          {/* Room - always show if available */}
                          {(() => {
                            const sessionRoom = getSessionRoomForDate(entry, cellDate);
                            return sessionRoom?.name && (
                              <div className="flex items-center gap-0.5 text-[8px] text-gray-500">
                                <DoorOpen size={8} />
                                <span className="truncate">{sessionRoom.name}</span>
                              </div>
                            );
                          })()}

                          {/* Shared session: the other groups taught together */}
                          {entry.sharedWith && entry.sharedWith.length > 0 && (
                            <div
                              className="flex items-center gap-0.5 text-[8px] text-indigo-600"
                              title={`Séance partagée avec ${entry.sharedWith.map((s) => `${s.groupeName} (${s.moduleElementName})`).join(', ')}`}
                            >
                              <Users size={8} />
                              <span className="truncate">+ {entry.sharedWith.map((s) => s.groupeName).join(', ')}</span>
                            </div>
                          )}

                          {/* Professor - use session's professor which may be overridden */}
                          {entryHeight > 50 && (() => {
                            const sessionProfessor = getSessionProfessorForDate(entry, cellDate);
                            return sessionProfessor && (
                              <div className="flex items-center gap-0.5 text-[8px] text-gray-500 mt-auto">
                                <User size={8} />
                                <span className="truncate">
                                  {sessionProfessor.firstName?.charAt(0)}. {sessionProfessor.lastName}
                                </span>
                              </div>
                            );
                          })()}
                        </div>
                      </button>
                      );
                    })}
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TimetableGrid;
